import { useCallback, useEffect, useRef, useState } from 'react';
import { roundHalfUpCents } from '../money';

/**
 * Number, Currency and Percent input (piece 3): one component at three
 * precisions and suffixes. Built on text with a decimal keypad, because
 * type=number discards what it cannot parse; here an unparseable entry is
 * kept on screen and flagged, never silently cleared. Currency edits in
 * dollars and stores integer cents, and the conversion lives here only.
 */
export type NumericKind = 'number' | 'currency' | 'percent';

const ADORNMENTS = /[,\s$%]/g;

/** Parse a typed decimal; grouping commas, spaces and the $ and % adornments are stripped first. Null means unparseable or empty. */
export function parseDecimal(text: string): number | null {
  const cleaned = text.replace(ADORNMENTS, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  if (!/^-?\d*(\.\d*)?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function dollarsToCents(dollars: number): number {
  return roundHalfUpCents(dollars * 100);
}

export function centsToDollars(cents: number): number {
  return cents / 100;
}

/** Quantity is two decimal places (decision 0058); currency is cents; percent defaults to two as well. */
export function defaultDecimals(_kind: NumericKind): number {
  return 2;
}

/** T2962: one formatter per decimals, built once and held. */
const numericFormatters = new Map<number, Intl.NumberFormat>();

/** Format for display with grouping and a fixed number of decimals; the suffix is the renderer's adornment, not part of the text. */
export function formatNumeric(value: number | null, decimals: number): string {
  if (value === null) return '';
  let held = numericFormatters.get(decimals);
  if (!held) {
    held = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: true });
    numericFormatters.set(decimals, held);
  }
  return held.format(value);
}

export interface NumericInputOptions {
  kind: NumericKind;
  /** Stored units: integer cents for currency, the number itself otherwise. Null is empty. */
  value: number | null;
  onChange: (value: number | null) => void;
  decimals?: number;
}

function toDisplayUnits(kind: NumericKind, stored: number | null): number | null {
  if (stored === null) return null;
  return kind === 'currency' ? centsToDollars(stored) : stored;
}

function toStoredUnits(kind: NumericKind, typed: number | null): number | null {
  if (typed === null) return null;
  return kind === 'currency' ? dollarsToCents(typed) : typed;
}

export function useNumericInput({ kind, value, onChange, decimals }: NumericInputOptions) {
  const places = decimals ?? defaultDecimals(kind);
  const [text, setText] = useState(() => formatNumeric(toDisplayUnits(kind, value), places));
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  const [invalid, setInvalid] = useState(false);

  /*
   * 🔨 The text follows the stored value only when the value, kind or places actually move. The first text is already
   * drawn from them, so the mount's own run of this effect writes nothing: run late, as it can when the first render
   * lands outside a browser event, it would blank a reading typed before it ran (found while building, 2026-10-09).
   */
  const synced = useRef({ kind, value, places });
  useEffect(() => {
    const was = synced.current;
    if (was.kind === kind && was.value === value && was.places === places) return;
    synced.current = { kind, value, places };
    if (!editingRef.current) setText(formatNumeric(toDisplayUnits(kind, value), places));
  }, [kind, value, places]);

  const beginEdit = useCallback(() => {
    editingRef.current = true;
    setEditing(true);
  }, []);
  const type = useCallback((next: string) => {
    setText(next);
    setInvalid(next.trim() !== '' && parseDecimal(next) === null);
  }, []);
  /** Commit on blur: a parseable entry is stored and reformatted; an unparseable one stays on screen, flagged. */
  const commit = useCallback(() => {
    editingRef.current = false;
    setEditing(false);
    if (text.trim() === '') {
      setInvalid(false);
      onChange(null);
      return;
    }
    const parsed = parseDecimal(text);
    if (parsed === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    const stored = toStoredUnits(kind, parsed);
    onChange(stored);
    setText(formatNumeric(toDisplayUnits(kind, stored), places));
  }, [text, kind, places, onChange]);

  return {
    text,
    invalid,
    editing,
    beginEdit,
    type,
    commit,
    suffix: kind === 'percent' ? '%' : null,
    prefix: kind === 'currency' ? '$' : null,
  };
}
